import React, { useState, useEffect } from 'react';
import { Clock, Plus, Trash2, Info, Lock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useCreateScheduleTemplate, useUpdateScheduleTemplate } from '@/hooks/schedules/useScheduleTemplates';
import type { DaysPattern, ScheduleTemplate, ShiftPattern, CustomPattern } from '@/types/schedule.types';
import { ShiftScheduleForm } from './ShiftScheduleForm';
import { CustomScheduleForm } from './CustomScheduleForm';
import { startOfMonthAppDateString, todayAppDateString } from '@/utils/dateFormat';

interface TemplateFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  mode: 'create' | 'edit';
  template?: ScheduleTemplate | null;
}

const WEEKDAYS = [
  { key: 'monday', label: 'Понедельник' },
  { key: 'tuesday', label: 'Вторник' },
  { key: 'wednesday', label: 'Среда' },
  { key: 'thursday', label: 'Четверг' },
  { key: 'friday', label: 'Пятница' },
  { key: 'saturday', label: 'Суббота' },
  { key: 'sunday', label: 'Воскресенье' },
] as const;

const DEFAULT_WORKING_PATTERN = {
  start: '09:00',
  end: '18:00',
  isWorking: true,
};

const DEFAULT_DAY_OFF_PATTERN = {
  start: undefined,
  end: undefined,
  isWorking: false,
};

const DEFAULT_DAYS_PATTERN: DaysPattern = {
  monday: DEFAULT_WORKING_PATTERN,
  tuesday: DEFAULT_WORKING_PATTERN,
  wednesday: DEFAULT_WORKING_PATTERN,
  thursday: DEFAULT_WORKING_PATTERN,
  friday: DEFAULT_WORKING_PATTERN,
  saturday: DEFAULT_DAY_OFF_PATTERN,
  sunday: DEFAULT_DAY_OFF_PATTERN,
};

export const TemplateFormDialog: React.FC<TemplateFormDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
  mode,
  template,
}) => {
  const [name, setName] = useState('');
  const [recurrenceType, setRecurrenceType] = useState<'weekly' | 'custom' | 'shift'>('weekly');
  const [daysPattern, setDaysPattern] = useState<DaysPattern>(DEFAULT_DAYS_PATTERN);
  const [shiftPattern, setShiftPattern] = useState<ShiftPattern>({
    type: 'shift',
    work_days: 2,
    off_days: 2,
    work_time: { start: '09:00', end: '18:00' },
    cycle_start_date: todayAppDateString(),
  });
  const [customPattern, setCustomPattern] = useState<CustomPattern>({
    type: 'custom',
    cycle_length: 4,
    cycle_start_date: startOfMonthAppDateString(), // First day of current month
    pattern: [
      { day: 1, isWorking: true, start: '08:00', end: '20:00' },
      { day: 2, isWorking: true, start: '20:00', end: '08:00' },
      { day: 3, isWorking: false },
      { day: 4, isWorking: false },
    ],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createTemplate = useCreateScheduleTemplate();
  const updateTemplate = useUpdateScheduleTemplate();

  // Check if template is from 1C (read-only mode)
  const isFrom1C = Boolean(mode === 'edit' && template?.code_1c);

  // Initialize form when editing
  useEffect(() => {
    if (mode === 'edit' && template && open) {
      setName(template.name);
      setRecurrenceType(template.recurrence_type);
      // Initialize pattern based on recurrence_type
      if (template.recurrence_type === 'shift' && (template.days_pattern as any)?.type === 'shift') {
        setShiftPattern(template.days_pattern as ShiftPattern);
      } else if (template.recurrence_type === 'custom' && (template.days_pattern as any)?.type === 'custom') {
        setCustomPattern(template.days_pattern as CustomPattern);
      } else {
        setDaysPattern(template.days_pattern as DaysPattern || DEFAULT_DAYS_PATTERN);
      }
    } else if (mode === 'create' && open) {
      // Reset form for create mode
      setName('');
      setRecurrenceType('weekly');
      setDaysPattern(DEFAULT_DAYS_PATTERN);
    }
  }, [mode, template, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = 'Название обязательно';
    }

    // Validate based on recurrence_type
    if (recurrenceType === 'weekly') {
      // Validate at least one working day for weekly
      const hasWorkingDay = Object.values(daysPattern).some(day => day?.isWorking);
      if (!hasWorkingDay) {
        newErrors.pattern = 'Должен быть хотя бы один рабочий день';
      }

      // Validate working days have times
      for (const [dayKey, dayData] of Object.entries(daysPattern)) {
        if (dayData?.isWorking) {
          if (!dayData.start || !dayData.end) {
            newErrors[dayKey] = 'Укажите время начала и конца';
          }
        }
      }
    } else if (recurrenceType === 'shift') {
      // Validate shift pattern
      if (shiftPattern.work_days < 1) {
        newErrors.work_days = 'Минимум 1 рабочий день';
      }
      if (shiftPattern.off_days < 1) {
        newErrors.off_days = 'Минимум 1 выходной день';
      }
      if (!shiftPattern.work_time.start || !shiftPattern.work_time.end) {
        newErrors.work_time = 'Укажите рабочее время';
      }
      if (!shiftPattern.cycle_start_date) {
        newErrors.cycle_start_date = 'Укажите дату начала цикла';
      }
    } else if (recurrenceType === 'custom') {
      // Validate custom pattern
      if (customPattern.cycle_length < 1) {
        newErrors.cycle_length = 'Минимум 1 день в цикле';
      }
      if (!customPattern.cycle_start_date) {
        newErrors.cycle_start_date = 'Укажите дату начала цикла';
      }
      if (customPattern.pattern.length === 0) {
        newErrors.pattern = 'Настройте расписание для дней цикла';
      }
      // Validate working days have times
      for (const dayPattern of customPattern.pattern) {
        if (dayPattern.isWorking && (!dayPattern.start || !dayPattern.end)) {
          newErrors[`day_${dayPattern.day}`] = `День ${dayPattern.day}: укажите время`;
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      // Format pattern based on recurrence_type
      let formattedPattern: any;
      
      if (recurrenceType === 'shift') {
        formattedPattern = shiftPattern;
      } else if (recurrenceType === 'custom') {
        formattedPattern = customPattern;
      } else {
        // Weekly pattern (default)
        formattedPattern = {};
        for (const [key, value] of Object.entries(daysPattern)) {
          if (value) {
            formattedPattern[key] = {
              start: value.start,
              end: value.end,
              isWorking: value.isWorking,
            };
          }
        }
      }

      if (mode === 'create') {
        await createTemplate.mutateAsync({
          name: name.trim(),
          recurrence_type: recurrenceType,
          days_pattern: formattedPattern as any,
        });
      } else {
        // Edit mode
        if (!template?.id) {
          setErrors({ submit: 'Шаблон не найден' });
          return;
        }
        await updateTemplate.mutateAsync({
          id: template.id,
          data: {
            name: name.trim(),
            recurrence_type: recurrenceType,
            days_pattern: formattedPattern as any,
          },
        });
      }

      // Reset form
      setName('');
      setDaysPattern(DEFAULT_DAYS_PATTERN);
      setRecurrenceType('weekly');

      onSuccess?.();
      onOpenChange(false);
    } catch (error: any) {
      setErrors({
        submit: error?.response?.data?.message || 
          (mode === 'create' ? 'Не удалось создать шаблон' : 'Не удалось обновить шаблон'),
      });
    }
  };

  const toggleDayWorking = (dayKey: keyof DaysPattern) => {
    setDaysPattern(prev => {
      const currentDay = prev[dayKey];
      const isWorking = !currentDay?.isWorking;

      return {
        ...prev,
        [dayKey]: isWorking
          ? { ...DEFAULT_WORKING_PATTERN }
          : { ...DEFAULT_DAY_OFF_PATTERN },
      };
    });
  };

  const updateDayTime = (dayKey: keyof DaysPattern, field: 'start' | 'end', value: string) => {
    setDaysPattern(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey]!,
        [field]: value,
      },
    }));
  };

  const applyToAllDays = () => {
    const mondayPattern = daysPattern.monday;
    if (!mondayPattern) return;

    const newPattern: DaysPattern = {};
    WEEKDAYS.forEach(({ key }) => {
      newPattern[key as keyof DaysPattern] = { ...mondayPattern };
    });

    setDaysPattern(newPattern);
  };

  const applyWorkdaysPattern = () => {
    setDaysPattern(DEFAULT_DAYS_PATTERN);
  };

  const handleClose = () => {
    if (!createTemplate.isPending && !updateTemplate.isPending) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div>
            <DialogTitle>
              {mode === 'create' ? 'Создать шаблон графика' : 'Редактировать шаблон графика'}
            </DialogTitle>
            <DialogDescription>
              Настройте недельный график работы для сотрудников
            </DialogDescription>
          </div>
          {!createTemplate.isPending && !updateTemplate.isPending && (
            <DialogClose onClose={handleClose} />
          )}
        </DialogHeader>

        {/* 🔒 1C Template Warning */}
        {isFrom1C && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20 p-4">
            <div className="flex items-start gap-3">
              <Lock className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                  Шаблон синхронизирован из 1С
                </h4>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Этот шаблон импортирован из системы 1С. Редактирование расписания выполняется только в 1С с последующей синхронизацией. 
                  Вы можете только просматривать параметры.
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Название шаблона <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например: 5/2 (9:00-18:00)"
              className={errors.name ? 'border-destructive' : ''}
              disabled={isFrom1C}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          {/* Recurrence Type */}
          <div className="space-y-2">
            <Label htmlFor="recurrence_type">Тип повторения</Label>
            <Select value={recurrenceType} onValueChange={(v: any) => setRecurrenceType(v)} disabled={isFrom1C}>
              <SelectTrigger id="recurrence_type" disabled={isFrom1C}>
                <SelectValue placeholder="Выберите тип" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Еженедельный</SelectItem>
                <SelectItem value="shift">Сменный</SelectItem>
                <SelectItem value="custom">Пользовательский</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Conditional form rendering based on recurrence_type */}
          {recurrenceType === 'shift' ? (
            /* Shift schedule form */
            <ShiftScheduleForm
              pattern={shiftPattern}
              onChange={setShiftPattern}
              disabled={isFrom1C}
            />
          ) : recurrenceType === 'custom' ? (
            /* Custom schedule form - mixed day/night shifts */
            <CustomScheduleForm
              pattern={customPattern}
              onChange={setCustomPattern}
              disabled={isFrom1C}
            />
          ) : (
            /* Weekly schedule form */
            <>
              {/* Quick actions */}
              {!isFrom1C && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={applyWorkdaysPattern}
                  >
                    5/2 (Пн-Пт рабочие)
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={applyToAllDays}
                  >
                    Применить Пн ко всем дням
                  </Button>
                </div>
              )}

              {/* Days pattern */}
              <div className="space-y-2">
                <Label>
                  Расписание по дням недели <span className="text-destructive">*</span>
                </Label>
                {errors.pattern && (
                  <p className="text-sm text-destructive">{errors.pattern}</p>
                )}
                <div className="space-y-3 rounded-lg border p-4">
                  {WEEKDAYS.map(({ key, label }) => {
                    const dayData = daysPattern[key as keyof DaysPattern];
                    const isWorking = dayData?.isWorking || false;

                    return (
                      <div
                        key={key}
                        className="flex items-center gap-3 rounded-md border p-3 bg-card"
                      >
                        <div className="flex items-center gap-2 w-40">
                          <Switch
                            checked={isWorking}
                            onCheckedChange={() => toggleDayWorking(key as keyof DaysPattern)}
                            disabled={isFrom1C}
                          />
                          <span className="text-sm font-medium">{label}</span>
                        </div>

                        {isWorking ? (
                          <div className="flex items-center gap-2 flex-1">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <Input
                              type="time"
                              value={dayData?.start || ''}
                              onChange={(e) =>
                                updateDayTime(key as keyof DaysPattern, 'start', e.target.value)
                              }
                              className={`w-32 ${errors[key] ? 'border-destructive' : ''}`}
                              disabled={isFrom1C}
                            />
                            <span className="text-muted-foreground">—</span>
                            <Input
                              type="time"
                              value={dayData?.end || ''}
                              onChange={(e) =>
                                updateDayTime(key as keyof DaysPattern, 'end', e.target.value)
                              }
                              className={`w-32 ${errors[key] ? 'border-destructive' : ''}`}
                              disabled={isFrom1C}
                            />
                          </div>
                        ) : (
                          <div className="flex-1 text-sm text-muted-foreground">
                            Выходной день
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {errors.submit && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {errors.submit}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createTemplate.isPending || updateTemplate.isPending}
            >
              {isFrom1C ? 'Закрыть' : 'Отмена'}
            </Button>
            {!isFrom1C && (
              <Button type="submit" disabled={createTemplate.isPending || updateTemplate.isPending}>
                {mode === 'create' 
                  ? (createTemplate.isPending ? 'Создание...' : 'Создать шаблон')
                  : (updateTemplate.isPending ? 'Сохранение...' : 'Сохранить изменения')
                }
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Backward compatibility export
export const CreateTemplateDialog = TemplateFormDialog;
